import React from 'react';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  MenuItemProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslate } from 'hooks/use-translate';
import { usePrescriptionCanComplete } from 'components/dialog-prescription-completeness';
import { WizardValidity } from 'components/dialog-prescription-completeness/components';
import { TemplateBooleanIcon } from 'components/templates/template-boolean-icon';
import { Stack } from 'components/stack';
import clsx from 'clsx';
import style from './index.module.scss';

type Props<D extends React.ElementType = 'li', P = {}> = MenuItemProps<D, P> & {
  prescriptionID: string;
  ignoreDisabled?: boolean;
  // MUI 9 types MenuItem's children on ButtonBase, behind a generic Omit TS cannot resolve here
  children?: React.ReactNode;
};
export const BaseMenuItem = <D extends React.ElementType = 'li', P = {}>({
  children,
  prescriptionID,
  ignoreDisabled,
  ...rest
}: Props<D, P>) => {
  const { tp } = useTranslate();

  const { isValid, canComplete, items, isLoading } = usePrescriptionCanComplete(prescriptionID);
  const disabled = ignoreDisabled ? false : rest.disabled || isLoading || !canComplete;
  const { onClick } = rest as { onClick?: React.MouseEventHandler<HTMLLIElement> };

  return (
    <MenuItem
      {...rest}
      className={clsx(style.root, rest.className)}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <Stack
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography>{children}</Typography>
        <Tooltip
          title={
            <WizardValidity
              title={tp('prescription-validity')}
              isValid={isValid}
              isLoading={isLoading}
              options={items}
            />
          }
        >
          <IconButton size={'small'}>
            {isLoading ? <CircularProgress size={12} /> : <TemplateBooleanIcon value={isValid} />}
          </IconButton>
        </Tooltip>
      </Stack>
    </MenuItem>
  );
};
